// Módulo PURO (sin imports, sin DB) de «Clientes por cara» (cadena huayruro-ojo-oido-agentes, tramo 10; sección 18
// del plan del ojo; Kevin, 19-oct-2026).
//
// Una webcam en el mostrador mira al cliente; la compu de la botica saca una HUELLA de 512 números (EdgeFace XXS,
// norma 1) y manda SOLO eso. La foto nunca sale de la compu. Aquí viven la lista cerrada de lo que entra, el
// parecido (coseno), cómo se guarda la huella (512 float32 = 2,048 bytes) y el texto del aviso que el cliente acepta.
//
// INVARIANTE 2 de la cadena: la cara nunca entra a un `caso` ni identifica personal. Reconocer = SUGERIR; quien
// atiende confirma (D-C6).

export const DIMENSION_HUELLA = 512;
export const BYTES_HUELLA = DIMENSION_HUELLA * 4;

// Umbral inicial (D-C4). Control del 19-oct-2026 con fotos de frente: misma persona ≥ 0.48, distintas ≤ 0.18.
// Se calibra con los primeros 20 clientes reales (tramo 11).
export const UMBRAL_PARECIDO = 0.4;
// Si dos clientes DISTINTOS quedan a menos de esto del mejor, no se sugiere a nadie: un nombre equivocado en una
// botica (alergias, crónicos) es peor que no sugerir.
export const MARGEN_AMBIGUO = 0.05;
// Las huellas de un registro tienen que parecerse entre sí (tres tomas de la misma persona).
export const MISMA_PERSONA_REGISTRO = 0.5;
export const MAX_HUELLAS_REGISTRO = 5;
// Cuánto vive un «Acepto» esperando las fotos de la webcam, y cuánto vive una sugerencia en el panel.
export const MINUTOS_ESPERA_REGISTRO = 2;
export const SEGUNDOS_SUGERENCIA = 120;
// La misma persona no se vuelve a sugerir en la misma botica antes de esto (ya se confirmó o se dijo «No»).
export const MINUTOS_SIN_REPETIR = 30;

// Texto del aviso que se muestra en la pantalla del mostrador antes del «Acepto». Es la CONSTANCIA de lo que la
// persona aceptó: se guarda tal cual en `cliente_consentimiento.texto` con su versión. BORRADOR de Claude
// (19-oct-2026) — Kevin lo aprueba y se valida contra la Ley 29733 y su reglamento (DS 016-2024-JUS) ANTES de
// publicar (sección 18.5). Si se reformula, sube la versión: los consentimientos viejos conservan su texto.
export const VERSION_AVISO_ROSTRO = "rostro-v1-borrador-2026-10-19";
export const TEXTO_AVISO_ROSTRO =
  "Para reconocerlo cuando vuelva y atenderlo más rápido, Botica Huayruro puede guardar una huella de su cara " +
  "(números sacados de una foto; la foto se borra en esta computadora y no se envía). La huella se usa solo para " +
  "reconocerlo en nuestras boticas, nunca para publicidad ni se comparte con nadie. Puede retirar su permiso " +
  "cuando quiera y la borramos. ¿Acepta?";

export type ValidacionRostro<T> = { ok: true; valor: T } | { ok: false; error: string };

const esNumero = (x: unknown): x is number => typeof x === "number" && Number.isFinite(x);
const ID_RE = /^[0-9A-Za-z-]{8,64}$/;

export function validarHuella(x: unknown): ValidacionRostro<number[]> {
  if (!Array.isArray(x) || x.length !== DIMENSION_HUELLA) return { ok: false, error: `huella: lista de ${DIMENSION_HUELLA} números` };
  let norma = 0;
  for (const v of x) {
    if (!esNumero(v) || Math.abs(v) > 1.0001) return { ok: false, error: "huella: solo números entre -1 y 1" };
    norma += v * v;
  }
  // EdgeFace devuelve la huella normalizada; una norma lejos de 1 es basura o una huella armada a mano.
  if (Math.abs(Math.sqrt(norma) - 1) > 0.01) return { ok: false, error: "huella: norma distinta de 1" };
  return { ok: true, valor: x as number[] };
}

const validarCalidad = (q: unknown): q is number => esNumero(q) && q >= 0 && q <= 1;

export type BuscarRostro = { huella: number[]; calidad: number | null };

// Lista CERRADA: `huella` y `calidad`, nada más. Un campo de más (foto, miniatura, nombre…) → error, no se ignora.
export function validarBuscarRostro(raw: unknown): ValidacionRostro<BuscarRostro> {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return { ok: false, error: "cuerpo inválido" };
  const o = raw as Record<string, unknown>;
  const extra = Object.keys(o).filter((k) => k !== "huella" && k !== "calidad");
  if (extra.length) return { ok: false, error: `campos no permitidos: ${extra.join(", ")}` };
  const h = validarHuella(o.huella);
  if (!h.ok) return h;
  if (o.calidad !== undefined && !validarCalidad(o.calidad)) return { ok: false, error: "calidad: número entre 0 y 1" };
  return { ok: true, valor: { huella: h.valor, calidad: o.calidad === undefined ? null : (o.calidad as number) } };
}

export type RegistroRostro = { consentimientoId: string; huellas: number[][]; calidad: number };

export function validarRegistroRostro(raw: unknown): ValidacionRostro<RegistroRostro> {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return { ok: false, error: "cuerpo inválido" };
  const o = raw as Record<string, unknown>;
  const extra = Object.keys(o).filter((k) => !["consentimiento_id", "huellas", "calidad"].includes(k));
  if (extra.length) return { ok: false, error: `campos no permitidos: ${extra.join(", ")}` };
  if (typeof o.consentimiento_id !== "string" || !ID_RE.test(o.consentimiento_id)) return { ok: false, error: "consentimiento_id inválido" };
  if (!Array.isArray(o.huellas) || o.huellas.length < 1 || o.huellas.length > MAX_HUELLAS_REGISTRO) return { ok: false, error: `huellas: de 1 a ${MAX_HUELLAS_REGISTRO}` };
  const huellas: number[][] = [];
  for (const x of o.huellas) {
    const h = validarHuella(x);
    if (!h.ok) return h;
    huellas.push(h.valor);
  }
  for (let i = 0; i < huellas.length; i++)
    for (let j = i + 1; j < huellas.length; j++)
      if (coseno(huellas[i]!, huellas[j]!) < MISMA_PERSONA_REGISTRO) return { ok: false, error: "las tomas no parecen la misma persona" };
  if (!validarCalidad(o.calidad)) return { ok: false, error: "calidad: número entre 0 y 1" };
  return { ok: true, valor: { consentimientoId: o.consentimiento_id, huellas, calidad: o.calidad } };
}

type Vector = ArrayLike<number>;

export function coseno(a: Vector, b: Vector): number {
  const n = Math.min(a.length, b.length);
  let s = 0;
  for (let i = 0; i < n; i++) s += a[i]! * b[i]!;
  return s;
}

// La huella se guarda como 512 float32 little-endian (2,048 bytes), siempre re-normalizada.
export function huellaABytes(h: Vector): Uint8Array {
  let norma = 0;
  for (let i = 0; i < h.length; i++) norma += h[i]! * h[i]!;
  norma = Math.sqrt(norma) || 1;
  const buf = new ArrayBuffer(BYTES_HUELLA);
  const dv = new DataView(buf);
  for (let i = 0; i < DIMENSION_HUELLA; i++) dv.setFloat32(i * 4, (h[i] ?? 0) / norma, true);
  return new Uint8Array(buf);
}

// D1 devuelve un BLOB como ArrayBuffer o como lista de bytes según la versión: se aceptan los dos.
export function bytesAHuella(b: ArrayBuffer | ArrayBufferView | ArrayLike<number>): Float32Array {
  const bytes =
    b instanceof ArrayBuffer ? new Uint8Array(b) : ArrayBuffer.isView(b) ? new Uint8Array(b.buffer, b.byteOffset, b.byteLength) : Uint8Array.from(b as ArrayLike<number>);
  if (bytes.byteLength !== BYTES_HUELLA) throw new Error(`huella guardada de ${bytes.byteLength} bytes (se esperaban ${BYTES_HUELLA})`);
  const dv = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const out = new Float32Array(DIMENSION_HUELLA);
  for (let i = 0; i < DIMENSION_HUELLA; i++) out[i] = dv.getFloat32(i * 4, true);
  return out;
}

export type CandidatoRostro = { clienteId: string; huella: Vector };
export type CoincidenciaRostro = { clienteId: string; parecido: number };

// El mejor cliente por encima del umbral, o null. Un cliente puede tener varias huellas (las tomas del registro):
// cuenta la más parecida. Si otro cliente DISTINTO queda a menos de MARGEN_AMBIGUO del mejor, null (ambiguo).
export function mejorCoincidencia(huella: Vector, candidatos: CandidatoRostro[], umbral = UMBRAL_PARECIDO, margen = MARGEN_AMBIGUO): CoincidenciaRostro | null {
  const porCliente = new Map<string, number>();
  for (const c of candidatos) {
    const p = coseno(huella, c.huella);
    if (p > (porCliente.get(c.clienteId) ?? -2)) porCliente.set(c.clienteId, p);
  }
  const orden = [...porCliente.entries()].sort((x, y) => y[1] - x[1]);
  const [mejor, segundo] = orden;
  if (!mejor || mejor[1] < umbral) return null;
  if (segundo && mejor[1] - segundo[1] < margen) return null;
  return { clienteId: mejor[0], parecido: Math.round(mejor[1] * 1000) / 1000 };
}
